import React from 'react'
import { observer } from 'mobx-react-lite'

import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { CategoryIcon, Text } from '@mxenabled/mxui'

import {
  Drawer,
  useAppConfig,
  useGlobalCopyStore,
  useTrendsStore,
  useTransactionStore,
  useScreenSize,
} from '../../common'
import { type Transaction } from '../../core'

import { getCategoryColor } from '../../core/utils/CategoryUtil'
import { ExportCsvAction, TransactionDetails, TransactionList } from '../../transactions'

import CategoryDetailChart from './CategoryDetailChart'
import { formatCurrency } from '../../core/utils/NumberFormatting'

export const CategoryDetails = observer(() => {
  const theme = useTheme()
  const { is_mobile_webview } = useAppConfig()
  const { isDesktop, isSmallTablet, isTablet } = useScreenSize()
  const { common: commonCopy, trends: copy } = useGlobalCopyStore()
  const { selectedCategoryData, selectedDateRangeMonthRange } = useTrendsStore()
  const { sortedTransactions: transactions } = useTransactionStore()

  const [transactionGuid, setTransactionGuid] = React.useState<string | null>(null)

  if (!selectedCategoryData) {
    return null
  }

  const { guid, name, is_income, totalAmount } = selectedCategoryData

  const chartColor = getCategoryColor(guid, theme)
  const chartLabel = is_income ? copy.income_label : copy.spending_label
  const transactionCountLabel = ` ${
    selectedCategoryData.transactions.length === 1 ? copy.transaction : copy.transactions
  } (${selectedCategoryData.transactions.length})`

  const transaction = React.useMemo(
    () => transactions.find((t) => t.guid === transactionGuid),
    [transactionGuid, transactions],
  )

  const shouldShowExportButton =
    transactions.length > 0 && !is_mobile_webview && (isDesktop || (isTablet && !isSmallTablet))

  const categoryFilter = (t: Transaction) =>
    t.category_guid === guid || t.top_level_category_guid === guid

  return (
    <Stack sx={{ alignItems: 'center', height: '100%' }}>
      <Stack sx={{ borderRadius: 0, pb: 4, pt: 16, px: 12, width: '100%' }}>
        <List sx={{ px: 12 }}>
          <ListItem>
            <ListItemAvatar>
              <CategoryIcon categoryGuid={guid} />
            </ListItemAvatar>
            <ListItemText
              primary={name}
              secondary={selectedDateRangeMonthRange}
              slotProps={{
                primary: { component: 'div', variant: 'h3', fontSize: 18, fontWeight: 700 },
                secondary: { component: 'div', variant: 'body2', fontSize: 13 },
              }}
            />
            <Text component="div" variant="h3">
              {formatCurrency(Math.abs(totalAmount), '0,0.00')}
            </Text>
          </ListItem>
        </List>

        <CategoryDetailChart chartColor={chartColor} chartLabel={chartLabel} />

        <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ mx: 12 }}>
          <Text bold={true} variant="body1">
            {transactionCountLabel}
          </Text>
          {shouldShowExportButton && <ExportCsvAction filter={categoryFilter} />}
        </Stack>
      </Stack>
      <TransactionList
        bgcolor="transparent"
        filter={categoryFilter}
        height="calc(100vh - 475px)"
        onClick={setTransactionGuid}
      />
      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={Boolean(transaction)}
        onClose={() => setTransactionGuid('')}
        title={copy.transaction}
      >
        {transaction && <TransactionDetails transaction={transaction} />}
      </Drawer>
    </Stack>
  )
})
